import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherArrowLeft } from "@subframe/core";
import { toast } from "sonner";

interface Props {
  notes: string;
  /** The server's reason for rejecting the submission, when it gave one. */
  reason?: string;
  onBack: () => void;
  onResubmit: () => void | Promise<void>;
}

/**
 * Rejected state — the server refused our completion while the task is still
 * open (e.g. a next due date outside the task's current window). A task that
 * someone else completed renders CompletionSummary instead, so this screen
 * never claims that. The user's answers are kept: they can return to the form,
 * correct what the server objected to, and submit again.
 */
export function CompletionConflict({
  notes,
  reason,
  onBack,
  onResubmit,
}: Props) {
  const handleCopyNotes = async () => {
    if (!notes.trim()) {
      toast.info("Nothing to copy — you didn't add any notes.");
      return;
    }
    try {
      await navigator.clipboard.writeText(notes);
      toast.success("Notes copied to clipboard");
    } catch {
      toast.error("Couldn't copy — try selecting and copying manually");
    }
  };

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      <div className="flex w-full items-center gap-2 pb-6">
        <IconButton
          variant="neutral-secondary"
          icon={<FeatherArrowLeft />}
          aria-label="Back"
          onClick={onBack}
        />
        <span className="grow shrink-0 basis-0 text-metrics font-metrics text-primary-navy-800">
          Not submitted
        </span>
      </div>

      <div className="flex w-full max-w-2xl mx-auto flex-col items-start gap-4 pb-12">
        <div className="flex w-full flex-col gap-3 rounded-md border-l-4 border-primary-navy-800 bg-primary-navy-50 px-4 py-3">
          <span className="text-body-bold font-body-bold text-primary-navy-800">
            We couldn&apos;t submit this task, so it&apos;s still open.
          </span>
          {reason && (
            <span className="text-body font-body text-neutral-900">
              {reason}
            </span>
          )}
          <span className="text-body font-body text-neutral-900">
            Your answers have been kept — edit them and submit again.
          </span>
        </div>

        {notes.trim() && (
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-caption font-caption text-subtext-color">
              Your notes
            </span>
            <div className="w-full whitespace-pre-wrap rounded-md border border-neutral-border bg-neutral-50 px-3 py-2 text-body font-body text-neutral-900">
              {notes}
            </div>
            <Button variant="neutral-secondary" onClick={handleCopyNotes}>
              Copy notes to clipboard
            </Button>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button onClick={onResubmit}>Edit and resubmit</Button>
        </div>
      </div>
    </div>
  );
}
